import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Chip
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Lightbulb as LightbulbIcon,
  Assignment as AssignmentIcon,
  Loyalty as LoyaltyIcon,
  Send as SendIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Import the BMC image
import bmcImage from '../assets/BMC.png';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.03);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Styled components
const BMCSection = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: color || '#f5f5f5',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  animation: `${fadeIn} 0.5s ease-out forwards`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '5px',
    background: theme.palette.primary.main,
    opacity: 0.7
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
  fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)'
  }
}));

const FloatingElement = styled(Box)(({ theme, delay = 0 }) => ({
  position: 'absolute',
  animation: `${float} 3s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  zIndex: 1
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
  }
}));

const Info = () => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setLoaded(true);
  }, []);

  // Updated BMC sections data for Warrili
  const bmcSections = [
    {
      id: 'key-partners',
      title: 'Key Partners',
      icon: <BusinessIcon />,
      color: '#e3f2fd',
      description: 'The network of suppliers and partners that make the Warrili business model work.',
      examples: [
        'AI and machine learning experts',
        'Hardware manufacturers (phone, computer, and TV repair parts suppliers)',
        'Cloud storage providers',
        'Payment gateways for secure transactions',
        'Online marketplaces (for sourcing spare parts)',
        'Customer support service providers',
        'Investors and funding partners'
      ],
      questions: [
        'Who are our key partners?',
        'Who are our key suppliers?',
        'Which key resources are we acquiring from partners?',
        'Which key activities do partners perform?'
      ]
    },
    {
      id: 'key-activities',
      title: 'Key Activities',
      icon: <AssignmentIcon />,
      color: '#e8f5e9',
      description: 'The most important things Warrili must do to make its business model work.',
      examples: [
        'AI development for fault detection and repair recommendations',
        'Image tracking and processing for diagnostics',
        'Cloud-based storage for user repair history',
        'Integrating a marketplace for spare parts and services',
        'Partnering with companies for direct product displays',
        'Ensuring secure payments and fraud prevention',
        'Customer support and AI-assisted guidance'
      ],
      questions: [
        'What key activities do our value propositions require?',
        'What activities are needed for our distribution channels?',
        'What activities are needed for customer relationships?',
        'What activities are needed for revenue streams?'
      ]
    },
    {
      id: 'key-resources',
      title: 'Key Resources',
      icon: <AccountBalanceIcon />,
      color: '#f3e5f5',
      description: 'The most important assets required to make the Warrili business model work.',
      examples: [
        'AI-powered fault detection models',
        'Cloud storage and computing infrastructure',
        'Mobile and web applications',
        'Partnerships with hardware and software providers',
        'Development team (AI engineers, software developers)',
        'Marketing and branding assets'
      ],
      questions: [
        'What key resources do our value propositions require?',
        'What resources are needed for distribution channels?',
        'What resources are needed for customer relationships?',
        'What resources are needed for revenue streams?'
      ]
    },
    {
      id: 'value-propositions',
      title: 'Value Propositions',
      icon: <LightbulbIcon />,
      color: '#fff8e1',
      description: 'The bundle of products and services that create value for Warrili\'s customer segments.',
      examples: [
        'Automated fault detection using AI for device issues',
        'Remote repair guidance with step-by-step AI solutions',
        'Cost-effective repairs by guiding users to affordable spare parts',
        'Paid service for businesses to showcase spare parts to customers',
        'Secure marketplace to connect users, suppliers, and investors',
        'AI-powered customer support for instant troubleshooting',
        'Social collaboration where users can share and discuss solutions'
      ],
      questions: [
        'What value do we deliver to the customer?',
        'Which one of our customer\'s problems are we helping to solve?',
        'What bundles of products and services are we offering?',
        'Which customer needs are we satisfying?'
      ]
    },
    {
      id: 'customer-relationships',
      title: 'Customer Relationships',
      icon: <LoyaltyIcon />,
      color: '#ffebee',
      description: 'The types of relationships Warrili establishes with specific customer segments.',
      examples: [
        'AI-driven chatbot and virtual assistant',
        'Live video consultations with repair experts',
        'Community forums for troubleshooting discussions',
        'Email and in-app notifications for repair updates',
        'Customer feedback loops for continuous improvement'
      ],
      questions: [
        'What type of relationship does each customer segment expect?',
        'Which ones have we established?',
        'How costly are they?',
        'How are they integrated with the rest of our business model?'
      ]
    },
    {
      id: 'channels',
      title: 'Channels',
      icon: <SendIcon />,
      color: '#e0f7fa',
      description: 'How Warrili communicates with and reaches its customer segments to deliver value propositions.',
      examples: [
        'Website and mobile app (for AI diagnostics and marketplace)',
        'Social media marketing (Facebook, Twitter, LinkedIn, YouTube)',
        'Paid ads and SEO optimization for organic reach',
        'Direct partnerships with electronics manufacturers and retailers',
        'Affiliate marketing with repair service providers'
      ],
      questions: [
        'Through which channels do our customer segments want to be reached?',
        'How are we reaching them now?',
        'How are our channels integrated?',
        'Which ones work best?'
      ]
    },
    {
      id: 'customer-segments',
      title: 'Customer Segments',
      icon: <GroupIcon />,
      color: '#f1f8e9',
      description: 'The different groups of people or organizations Warrili aims to reach and serve.',
      examples: [
        'Individual users needing device repair guidance',
        'DIY repair enthusiasts looking for spare parts',
        'Electronics repair shops needing smart diagnostic tools',
        'Businesses selling spare parts who want direct customer access',
        'Tech support companies integrating AI solutions'
      ],
      questions: [
        'For whom are we creating value?',
        'Who are our most important customers?',
        'What are the customer archetypes?'
      ]
    },
    {
      id: 'cost-structure',
      title: 'Cost Structure',
      icon: <AttachMoneyIcon />,
      color: '#fce4ec',
      description: 'All costs incurred to operate the Warrili business model.',
      examples: [
        'AI research and development',
        'Cloud infrastructure and storage costs',
        'Web and mobile app maintenance',
        'Marketing and customer acquisition',
        'Partner and supplier commissions',
        'Customer support operations'
      ],
      questions: [
        'What are the most important costs inherent in our business model?',
        'Which key resources are most expensive?',
        'Which key activities are most expensive?'
      ]
    },
    {
      id: 'revenue-streams',
      title: 'Revenue Streams',
      icon: <AttachMoneyIcon />,
      color: '#e8f5e9',
      description: 'The cash Warrili generates from each customer segment.',
      examples: [
        'Freemium model: Basic AI diagnostics for free',
        'Subscription plans for premium AI-powered repair assistance',
        'Commission-based revenue from marketplace sales',
        'Paid ads and sponsorships from repair companies and parts suppliers',
        'Affiliate partnerships with spare parts vendors',
        'Consultation fees for live expert advice'
      ],
      questions: [
        'For what value are our customers willing to pay?',
        'For what do they currently pay?',
        'How are they currently paying?',
        'How would they prefer to pay?'
      ]
    }
  ];

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setOpenDialog(true);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f9f9f9',
      backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 80%)',
      pt: 4, 
      pb: 8 
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Fade in={loaded} timeout={800}>
          <Box sx={{ 
            mb: 6, 
            textAlign: 'center',
            position: 'relative'
          }}>
            <FloatingElement 
              sx={{ 
                top: -30, 
                left: '10%', 
                width: 60, 
                height: 60, 
                borderRadius: '50%', 
                bgcolor: 'rgba(0, 105, 60, 0.1)' 
              }} 
              delay={0.5}
            />
            <FloatingElement 
              sx={{ 
                bottom: -20, 
                right: '15%', 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255, 227, 2, 0.15)' 
              }} 
              delay={1}
            />
            
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #006938, #00a651)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                fontFamily: '"Poppins", sans-serif',
                position: 'relative',
                display: 'inline-block'
              }}
            >
              Business Model Canvas – Warrili
            </Typography>
            
            <Typography 
              variant="h5" 
              color="text.secondary" 
              gutterBottom
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              A strategic management template for developing and documenting Warrili's AI-powered device repair business model
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <StyledChip 
                icon={<StarIcon />} 
                label="AI Diagnostics" 
                color="primary" 
                variant="outlined"
              />
              <StyledChip 
                icon={<BusinessIcon />} 
                label="Device Repair" 
                color="primary" 
                variant="outlined"
              />
              <StyledChip 
                icon={<LightbulbIcon />} 
                label="Smart Solutions" 
                color="primary" 
                variant="outlined"
              />
            </Box>
          </Box>
        </Fade>

        {/* Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mb: 3,
          gap: 1
        }}>
          <Tooltip title="Zoom Out" arrow>
            <IconButton 
              onClick={handleZoomOut} 
              color="primary"
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In" arrow>
            <IconButton 
              onClick={handleZoomIn} 
              color="primary"
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download" arrow>
            <IconButton 
              color="primary"
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print" arrow>
            <IconButton 
              color="primary"
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share" arrow>
            <IconButton 
              color="primary"
              sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* BMC Image */}
        <Zoom in={loaded} style={{ transitionDelay: loaded ? '300ms' : '0ms' }}>
          <ImageContainer sx={{ 
            mb: 6, 
            textAlign: 'center',
            overflow: 'hidden',
            bgcolor: 'white',
            animation: `${pulse} 6s infinite ease-in-out`
          }}>
            <Box sx={{ 
              transform: `scale(${zoomLevel})`,
              transition: 'transform 0.3s ease',
              transformOrigin: 'center center',
              p: 3
            }}>
              <img 
                src={bmcImage} 
                alt="Business Model Canvas - Warrili" 
                style={{ 
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>
          </ImageContainer>
        </Zoom>

        <Divider sx={{ 
          my: 6, 
          borderColor: 'rgba(0, 0, 0, 0.1)'
        }}>
          <Chip 
            label="Warrili Components" 
            color="primary" 
            sx={{ 
              fontWeight: 'bold',
              px: 2,
              py: 3
            }} 
          />
        </Divider>

        {/* BMC Sections */}
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom 
          fontWeight="bold" 
          sx={{ 
            mb: 4,
            textAlign: 'center',
            fontFamily: '"Poppins", sans-serif',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '2px'
            }
          }}
        >
          Warrili Business Model Canvas Components
        </Typography>

        <Grid container spacing={4}>
          {bmcSections.map((section, index) => (
            <Grid 
              item 
              xs={12} 
              md={6} 
              lg={4} 
              key={section.id}
              sx={{ 
                opacity: 0,
                animation: `${fadeIn} 0.5s ease-out forwards ${index * 0.1}s`
              }}
            >
              <BMCSection color={section.color}>
                <SectionTitle variant="h6">
                  {section.icon} {section.title}
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Learn More" arrow>
                    <IconButton 
                      size="small" 
                      onClick={() => handleSectionClick(section)}
                      sx={{ 
                        bgcolor: 'rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton 
                    size="small"
                    onClick={() => toggleSection(section.id)}
                    sx={{ 
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                    }}
                  >
                    {expandedSections[section.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </SectionTitle>
                
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    color: 'rgba(0, 0, 0, 0.7)',
                    lineHeight: 1.6,
                    fontFamily: '"Inter", sans-serif'
                  }}
                >
                  {section.description}
                </Typography>
                
                <Collapse in={expandedSections[section.id] || false}>
                  <Box sx={{ mt: 3 }}>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold"
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <LightbulbIcon fontSize="small" color="primary" />
                      Examples:
                    </Typography>
                    <List dense sx={{ bgcolor: 'rgba(255, 255, 255, 0.5)', borderRadius: 2, p: 1 }}>
                      {section.examples.slice(0, 3).map((example, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={example} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              fontFamily: '"Inter", sans-serif'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold" 
                      sx={{ 
                        mt: 2, 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <HelpIcon fontSize="small" color="secondary" />
                      Key Questions:
                    </Typography>
                    <List dense sx={{ bgcolor: 'rgba(255, 255, 255, 0.5)', borderRadius: 2, p: 1 }}>
                      {section.questions.slice(0, 2).map((question, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <InfoIcon fontSize="small" color="secondary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={question} 
                            primaryTypographyProps={{ 
                              fontSize: '0.9rem',
                              fontStyle: 'italic',
                              fontFamily: '"Inter", sans-serif'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={() => handleSectionClick(section)}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        mt: 2,
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                        }
                      }}
                    >
                      View All Details
                    </Button>
                  </Box>
                </Collapse>
              </BMCSection>
            </Grid>
          ))}
        </Grid>

        {/* Section Detail Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              overflow: 'hidden'
            }
          }}
        >
          {selectedSection && (
            <>
              <DialogTitle sx={{ 
                bgcolor: selectedSection.color,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 3,
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700
              }}>
                {selectedSection.icon} {selectedSection.title}
              </DialogTitle>
              <DialogContent dividers sx={{ p: 4 }}>
                <Typography 
                  variant="subtitle1" 
                  paragraph
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    mb: 3,
                    fontFamily: '"Inter", sans-serif'
                  }}
                >
                  {selectedSection.description}
                </Typography>
                
                <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: theme.palette.primary.main,
                        fontWeight: 700
                      }}
                    >
                      <LightbulbIcon color="primary" /> Examples
                    </Typography>
                    <List>
                      {selectedSection.examples.map((example, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={example} 
                            primaryTypographyProps={{ 
                              fontFamily: '"Inter", sans-serif'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
                
                <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: theme.palette.secondary.main,
                        fontWeight: 700
                      }}
                    >
                      <HelpIcon color="secondary" /> Key Questions to Consider
                    </Typography>
                    <List>
                      {selectedSection.questions.map((question, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <InfoIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={question} 
                            primaryTypographyProps={{ 
                              fontFamily: '"Inter", sans-serif'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default Info;